import { useMutation, useQuery } from '@tanstack/react-query'
import {
	Button,
	CycleAdminIcon,
	HierarchiesIcon,
	Link,
	MetricCard,
	RolesIcon,
	resolveCycleStatus,
	SpinnerWithBackground,
	StepsIcon,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	UsersIcon,
} from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'
import type { CycleParticipantGetRolesInput, CycleParticipantGetRolesOutput } from '../types'

export function CycleBuildCycleTab() {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const navigate = useNavigate()
	const { showToast } = useToast()
	const [isBuildEnabled, setIsBuildEnabled] = useState(false)

	const { data: generalConfigurations, isLoading } = useQuery({
		...trpc.admin.cycles.getGeneralConfigurations.queryOptions({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		}),
		enabled: !!organizationId && !!cycleId,
		refetchOnMount: true,
	})

	const { data: cycle, isLoading: loadingCycle } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({
			id: cycleId as string,
		}),
		enabled: !!cycleId,
	})

	useEffect(() => {
		if (cycle) {
			setIsBuildEnabled(cycle.status === 'draft')
		}
	}, [cycle])

	const navigateToTab = (tab: string) => {
		navigate(`/organizations/${organizationId}/cycles/${cycleId}/${tab}`)
	}

	const buildCycleMutation = useMutation({
		...trpc.admin.cycles.build.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle built successfully')
			setIsBuildEnabled(true)
		},
		onError: (e) => {
			showToast(e.message ? e.message : 'Something went wrong, cycle was not built', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.getById.queryKey({ id: cycleId }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const buildCycleHandler = async () => {
		buildCycleMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		})
	}

	if (isLoading || loadingCycle) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	const participantsColumns: Column<CycleParticipantGetRolesOutput>[] = [
		{
			field: 'firstName',
			label: 'Fist Name',
			colSpan: 1,
		},
		{
			field: 'lastName',
			label: 'Last Name',
			colSpan: 1,
		},
		{
			field: 'roleDisplayName',
			label: 'Role',
			colSpan: 1,
		},
	]

	return (
		<div className='mt-4'>
			<Text variant='medium' weight='semibold' className='text-sm text-left'>
				Review the general configurations
			</Text>

			<div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-4'>
				<MetricCard
					icon={CycleAdminIcon}
					title='Client admins'
					value={generalConfigurations?.adminsCount.toString() || ''}
					onClick={() => navigateToTab('admins')}
				/>
				<MetricCard
					icon={RolesIcon}
					title='Cycle roles'
					value={generalConfigurations?.rolesCount.toString() || ''}
					onClick={() => navigateToTab('roles')}
				/>
				<MetricCard
					icon={StepsIcon}
					title='Cycle steps'
					value={generalConfigurations?.cycleStepsCount.toString() || ''}
					onClick={() => navigateToTab('cycleSteps')}
				/>
			</div>
			<div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
				<MetricCard
					icon={UsersIcon}
					title='Cycle participants'
					value={generalConfigurations?.participantsCount.toString() || ''}
					onClick={() => navigateToTab('participants')}
				/>
				<MetricCard
					icon={HierarchiesIcon}
					title='Cycle hierarchy'
					value={generalConfigurations?.cycleHierarchyFile.name || ''}
					onClick={() => navigateToTab('hierarchies')}
				/>
			</div>
			<div className='flex justify-between items-center mt-4'>
				<Text variant='medium' weight='semibold' className='text-sm'>
					Cycle steps workflow
				</Text>
				<Link className='self-start ml-auto w-40' onClick={() => navigateToTab('cycleSteps')}>
					{'Edit cycle steps >'}
				</Link>
			</div>
			<div className='mt-8'>
				<Text variant='medium' weight='semibold' className='text-sm text-left'>
					Participants
				</Text>
				<div className='mt-4 mb-8'>
					<GenericTable<CycleParticipantGetRolesOutput, CycleParticipantGetRolesInput>
						queryOptionsGenerator={(options) => ({
							...trpc.admin.cycles.participants.roles.list.queryOptions({
								...options,
								organizationId: organizationId as string,
								cycleId: cycleId || '',
							}),
							enabled: !!organizationId && !!cycleId,
						})}
						columns={participantsColumns}
					/>
				</div>
			</div>
			<div className='mt-4'>
				<div className='flex w-full justify-between rounded-sm border border-primary-100 p-6 gap-x-3'>
					<Text variant='medium' weight='semibold'>
						Build cycle
					</Text>
					<Tooltip>
						<TooltipTrigger asChild>
							<span>
								<Button
									variant='primary'
									size='medium'
									onClick={buildCycleHandler}
									disabled={!isBuildEnabled || loadingCycle}
									className='self-center'>
									Build cycle
								</Button>
							</span>
						</TooltipTrigger>
						{!isBuildEnabled && !loadingCycle && (
							<TooltipContent side='top' className='max-w-48 text-center'>
								{cycle?.status
									? `Cycle is ${resolveCycleStatus(cycle.status)} and cannot be rebuilt`
									: 'Cycle cannot be rebuilt'}
							</TooltipContent>
						)}
					</Tooltip>
				</div>
			</div>
		</div>
	)
}
